<template>
  <!-- 히어로 배너 편집 드로어 -->
  <a-drawer
    :visible="isVisible"
    title="히어로 배너 편집"
    placement="right"
    :width="drawerWidth"
    :destroy-on-close="false"
    @close="handleClose"
  >
    <div v-if="heroData" class="hero-editor">
      <!-- 제목 -->
      <div class="hero-editor__field">
        <label class="hero-editor__label">제목</label>
        <a-input
          :value="heroData.title"
          :maxlength="50"
          show-count
          placeholder="히어로 제목을 입력하세요"
          @change="(e) => updateField('title', e.target.value)"
        />
      </div>

      <!-- 부제목 -->
      <div class="hero-editor__field">
        <label class="hero-editor__label">부제목</label>
        <a-textarea
          :value="heroData.subtitle"
          :maxlength="100"
          show-count
          :rows="3"
          placeholder="히어로 부제목을 입력하세요"
          @change="(e) => updateField('subtitle', e.target.value)"
        />
      </div>

      <!-- CTA 버튼 텍스트 -->
      <div class="hero-editor__field">
        <label class="hero-editor__label">CTA 버튼 텍스트</label>
        <a-input
          :value="heroData.ctaText"
          :maxlength="20"
          placeholder="버튼 텍스트를 입력하세요"
          @change="(e) => updateField('ctaText', e.target.value)"
        />
      </div>

      <!-- CTA 링크 URL -->
      <div class="hero-editor__field">
        <label class="hero-editor__label">CTA 링크 URL</label>
        <a-input
          :value="heroData.ctaUrl"
          type="url"
          placeholder="https://..."
          @change="(e) => updateField('ctaUrl', e.target.value)"
        />
      </div>

      <!-- 히어로 이미지 -->
      <div class="hero-editor__field">
        <label class="hero-editor__label">히어로 이미지</label>
        <image-uploader
          :model-value="heroData.heroImage"
          label=""
          :max-size-m-b="5"
          @update:model-value="(url) => updateField('heroImage', url)"
        />
      </div>

      <!-- 탭 목록 (5개 고정) -->
      <div class="hero-editor__field">
        <label class="hero-editor__label">탭 목록</label>
        <div
          v-for="(tab, idx) in heroTabs"
          :key="idx"
          class="hero-editor__tab-item"
        >
          <span class="hero-editor__tab-index">{{ idx + 1 }}</span>
          <div class="hero-editor__tab-fields">
            <a-input
              :value="tab.name"
              placeholder="탭 이름"
              size="small"
              @change="(e) => updateTab(idx, 'name', e.target.value)"
            />
            <a-input
              :value="tab.link"
              placeholder="탭 링크 URL"
              size="small"
              style="margin-top: 4px;"
              @change="(e) => updateTab(idx, 'link', e.target.value)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 데이터 미로드 상태 -->
    <div v-else class="hero-editor__empty">
      <a-spin tip="데이터를 불러오는 중..." />
    </div>
  </a-drawer>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ImageUploader from '../common/ImageUploader.vue';

// 기본 탭 구조 (5개)
const DEFAULT_TABS = Array.from({ length: 5 }, (_, i) => ({ name: `탭 ${i + 1}`, link: '' }));

export default {
  name: 'HeroEditor',

  components: {
    ImageUploader,
  },

  data() {
    return {
      // Esc 키 이벤트 핸들러 레퍼런스 (제거 시 필요)
      escHandler: null,
    };
  },

  computed: {
    ...mapState('homepageEditor', ['activeEditor']),
    ...mapGetters('homepageEditor', ['currentPageData']),

    // 드로어 표시 여부 (activeEditor가 hero 드로어인 경우)
    isVisible() {
      return (
        this.activeEditor?.type === 'drawer' &&
        this.activeEditor?.sectionKey === 'hero'
      );
    },

    // 반응형 드로어 너비 (1440px 이상 시 520px, 미만 시 420px)
    drawerWidth() {
      return window.innerWidth >= 1440 ? 520 : 420;
    },

    // 현재 히어로 섹션 데이터
    heroSection() {
      if (!this.currentPageData?.sections) return null;
      return this.currentPageData.sections.find((s) => s.sectionKey === 'hero');
    },

    // 히어로 데이터 객체
    heroData() {
      return this.heroSection?.data || null;
    },

    // 탭 배열 (없을 경우 기본값 사용)
    heroTabs() {
      if (!this.heroData?.tabs || this.heroData.tabs.length === 0) {
        return DEFAULT_TABS;
      }
      // 항상 5개 탭 유지
      const tabs = [...this.heroData.tabs];
      while (tabs.length < 5) {
        tabs.push({ name: '', link: '' });
      }
      return tabs.slice(0, 5);
    },
  },

  watch: {
    // 드로어가 열릴 때 Esc 키 리스너 등록
    isVisible(val) {
      if (val) {
        this.registerEscHandler();
      } else {
        this.removeEscHandler();
      }
    },
  },

  beforeUnmount() {
    this.removeEscHandler();
  },

  methods: {
    // 드로어 닫기 처리
    handleClose() {
      this.$store.commit('homepageEditor/SET_ACTIVE_EDITOR', null);
    },

    // 히어로 데이터 특정 필드 즉시 Vuex 반영
    updateField(field, value) {
      if (!this.heroSection) return;
      const updatedData = { ...this.heroData, [field]: value };
      this.$store.commit('homepageEditor/UPDATE_SECTION', {
        page: 'main',
        sectionKey: 'hero',
        data: updatedData,
      });
    },

    // 특정 탭 필드 업데이트 후 Vuex 반영
    updateTab(index, field, value) {
      const tabs = [...this.heroTabs];
      tabs[index] = { ...tabs[index], [field]: value };
      this.updateField('tabs', tabs);
    },

    // Esc 키 핸들러 등록
    registerEscHandler() {
      this.escHandler = (e) => {
        if (e.key === 'Escape') {
          this.handleClose();
        }
      };
      window.addEventListener('keydown', this.escHandler);
    },

    // Esc 키 핸들러 제거
    removeEscHandler() {
      if (this.escHandler) {
        window.removeEventListener('keydown', this.escHandler);
        this.escHandler = null;
      }
    },
  },
};
</script>

<style scoped>
.hero-editor__field {
  margin-bottom: 20px;
}

.hero-editor__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.hero-editor__tab-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

.hero-editor__tab-index {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e8e8e8;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  margin-top: 4px;
}

.hero-editor__tab-fields {
  flex: 1;
}

.hero-editor__empty {
  text-align: center;
  padding: 40px 0;
}
</style>
